import {
  FiltersSource,
  filtersSourceIsEnumerable,
  filtersSourceKey,
} from '@rivethealth/data/advanced-filters';
import { Filter, Order } from '@rivethealth/data/super-table';
import { omit } from 'lodash';
import { Subject } from 'rxjs/internal/Subject';
import { ChangeService } from '../core/change.service';

export class TableStateHelper<T> {
  loadChangeFilter = true;
  loadChangeSort = true;
  loadChangeTableView = true;
  constructor(
    private filters: Subject<T> | ((filters: T) => void),
    private sort: Subject<Order<string>> | ((sort: Order) => void) | undefined,
    private changeService: ChangeService,
    private name: string,
    private accountId: string,
    private tableView?: Subject<number | null>,
    private excludedFilters?: Partial<{
      [key in keyof T]: Filter<any[]>;
    }>,
  ) {}
  async tableSetup() {
    const readFilters = localStorage.getItem(
      this.name + '_filter:' + this.accountId,
    );
    const readSort = localStorage
      .getItem(this.name + '_sort:' + this.accountId)
      ?.split(',');
    const readTableView = localStorage.getItem(
      this.name + '_view:' + this.accountId,
    );
    if (window.location.search === '') {
      if (readFilters) {
        const filters = {
          ...JSON.parse(readFilters),
          ...this.excludedFilters,
        };
        const filtersSource: FiltersSource = {
          source: 'localStorage',
        };
        Object.defineProperty(filters, filtersSourceKey, {
          enumerable: filtersSourceIsEnumerable,
          value: filtersSource,
        });
        if (this.filters instanceof Subject) {
          // RIVET-8981 there is a race case somewhere and this
          // await timeout solves the issue. Another ticket will
          // investigate this further.
          await new Promise(r => setTimeout(r, 0));
          this.filters.next(filters);
        } else {
          this.filters(filters);
        }
      }
      if (readSort && this.sort) {
        if (this.sort instanceof Subject) {
          this.sort.next({
            name: readSort[0] || 'none',
            direction: readSort[1] == 'desc' ? 'desc' : 'asc',
          });
        } else {
          this.sort({
            name: readSort[0] || 'default',
            direction: readSort[1] == 'desc' ? 'desc' : 'asc',
          });
        }
      }
      if (
        this.tableView &&
        readTableView &&
        readTableView !== 'NaN' &&
        readTableView !== 'null'
      ) {
        this.tableView.next(Number(readTableView));
      }
    }
    if (this.filters instanceof Subject) {
      this.changeService.listen(this.filters, filter => {
        if (!this.loadChangeFilter) {
          this.saveFilters(filter);
        } else {
          this.loadChangeFilter = false;
        }
      });
    }
    if (this.tableView) {
      this.changeService.listen(this.tableView, tableView => {
        if (!this.loadChangeTableView) {
          localStorage.setItem(
            this.name + '_view:' + this.accountId,
            tableView + '',
          );
        } else {
          this.loadChangeTableView = false;
        }
      });
    }
    if (this.sort instanceof Subject) {
      this.changeService.listen(this.sort, sort => {
        if (!this.loadChangeSort) {
          localStorage.setItem(
            this.name + '_sort:' + this.accountId,
            sort.name + ',' + sort.direction,
          );
        } else {
          this.loadChangeSort = false;
        }
      });
    }
  }

  readFilters() {
    const readFilters = localStorage.getItem(
      this.name + '_filter:' + this.accountId,
    );
    if (readFilters) {
      return { ...JSON.parse(readFilters), ...this.excludedFilters };
    }
    return undefined;
  }

  saveFilters(filters: T) {
    localStorage.setItem(
      this.name + '_filter:' + this.accountId,
      JSON.stringify(
        this.excludedFilters
          ? omit(Object(filters), Object.keys(this.excludedFilters))
          : filters,
      ),
    );
  }
}
